import { API_ORIGIN, isNativeApp, nativeHeaders } from '@/lib/native';

/**
 * Fichiers servis par l'API (PDF, XML, exports CSV, justificatifs) dans l'app.
 * Sur le web, un simple lien suffit : le cookie de session l'accompagne. Dans
 * l'app, il n'y a pas de cookie : on télécharge avec le jeton, on écrit le
 * fichier dans le cache de l'appareil et on ouvre la feuille de partage
 * (Enregistrer dans Fichiers, Mail, WhatsApp, imprimer…).
 */

/** Nom tiré de `Content-Disposition` (filename* RFC 5987 d'abord). */
function filenameFrom(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (star?.[1]) return decodeURIComponent(star[1]);
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1] ? decodeURIComponent(plain[1]) : fallback;
}

async function fetchApiFile(path: string): Promise<{ blob: Blob; name: string }> {
  const response = await fetch(`${API_ORIGIN}${path}`, { headers: nativeHeaders(), credentials: 'omit' });
  if (!response.ok) throw new Error(`Téléchargement impossible (HTTP ${response.status})`);
  const fallback = path.split('?')[0]?.split('/').pop() || 'document';
  return { blob: await response.blob(), name: filenameFrom(response.headers.get('content-disposition'), fallback) };
}

const toBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

export async function shareApiFile(path: string): Promise<void> {
  const [{ Filesystem, Directory }, { Share }] = await Promise.all([
    import('@capacitor/filesystem'),
    import('@capacitor/share'),
  ]);
  const { blob, name } = await fetchApiFile(path);
  // Nom assaini : il sert de chemin dans le cache de l'app.
  const safeName = name.replace(/[\\/:*?"<>|]+/g, '-');
  const { uri } = await Filesystem.writeFile({
    path: `partages/${safeName}`,
    data: await toBase64(blob),
    directory: Directory.Cache,
    recursive: true,
  });
  await Share.share({ title: safeName, files: [uri] }).catch((error: unknown) => {
    // Feuille de partage fermée sans choix : ce n'est pas une erreur.
    if (!/cancel/i.test(String(error))) throw error;
  });
}

/** Image protégée (photo de justificatif) affichable dans un <img> : URL blob. */
export async function apiImageUrl(path: string): Promise<string> {
  if (!isNativeApp) return path;
  return URL.createObjectURL((await fetchApiFile(path)).blob);
}

/**
 * Dans l'app, intercepte tout lien vers `/api/…` (PDF, exports…) et le passe
 * par la feuille de partage — sans toucher aux nombreux liens des vues.
 */
export function interceptApiLinks(onError: (message: string) => void): void {
  if (!isNativeApp) return;
  document.addEventListener(
    'click',
    (event) => {
      const link = (event.target as Element | null)?.closest?.('a[href^="/api/"]');
      if (!link) return;
      event.preventDefault();
      shareApiFile(link.getAttribute('href')!).catch((error: unknown) =>
        onError(error instanceof Error ? error.message : 'Téléchargement impossible'),
      );
    },
    true,
  );
}
